import { cn } from "@/lib/utils";
import type React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import PaginationRow from "@/components/pagination-row";
import type { RowSelectionState } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "react-i18next";

interface MobileListWrapperProps<TData extends { id: string }> {
  data: TData[];
  isLoading: boolean;
  className?: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  emptyListComponent: React.ReactElement;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  renderItem: (item: TData) => React.ReactNode;
  onItemClick: (item: TData) => void;
}

function MobileListWrapper<TData extends { id: string }>({
  data,
  isLoading,
  className,
  page,
  pageCount,
  onPageChange,
  emptyListComponent,
  rowSelection,
  onRowSelectionChange,
  renderItem,
  onItemClick,
}: MobileListWrapperProps<TData>) {
  const { t } = useTranslation();
  const selectable = rowSelection !== undefined && onRowSelectionChange;

  const toggleRow = (index: number) => {
    if (!selectable) return;
    const key = String(index);
    const next = { ...rowSelection };
    if (next[key]) {
      delete next[key];
    } else {
      next[key] = true;
    }
    onRowSelectionChange(next);
  };

  return (
    <div className={cn(className)}>
      {isLoading && (
        <div className="flex flex-col gap-2 justify-center items-center">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-none" />
          ))}
        </div>
      )}
      {!isLoading && data.length === 0 && emptyListComponent}
      {!isLoading && data.length > 0 && (
        <>
          <ul className="divide-y">
            {data.map((item, index) => (
              <li key={item.id} className="flex items-center gap-4 px-4 py-2">
                {selectable && (
                  <Checkbox
                    checked={!!rowSelection[String(index)]}
                    onCheckedChange={() => toggleRow(index)}
                    aria-label={t("keys.select_row")}
                    className="shrink-0"
                  />
                )}
                <button
                  onClick={() => onItemClick(item)}
                  className="flex-1 text-left flex flex-col"
                >
                  {renderItem(item)}
                </button>
              </li>
            ))}
          </ul>
          <PaginationRow
            page={page}
            pageCount={pageCount}
            onPageChange={onPageChange}
          />
        </>
      )}
    </div>
  );
}

export default MobileListWrapper;
